import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Search, Star, MapPin, Filter, X, Building2, Home, Car, Navigation, Sparkles, TrendingUp, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { CuratedSection } from '../components/discovery/CuratedSection';
import { SpecialtyFilter } from '../components/discovery/SpecialtyFilter';
import { CATEGORIES as CATEGORY_DEFS, LEGACY_CATEGORY_MAP, resolveCategoryId } from '../lib/categories';

// Hoisted to module scope so React doesn't remount it on every Discovery
// render, nested component definitions break reconciliation and lose
// internal state on each parent re-render.
function SkeletonGrid({ keyPrefix }) {
  // Pinterest-style masonry skeleton with varied heights so the loading
  // state matches the real layout the user is about to see.
  const heights = ['aspect-[3/4]', 'aspect-[4/3]', 'aspect-[4/5]', 'aspect-square', 'aspect-[3/4]', 'aspect-[4/5]'];
  return (
    <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 lg:gap-3">
      {heights.map((h, i) => (
        <div key={`skeleton-${keyPrefix}-${i}`} className="break-inside-avoid mb-2 lg:mb-3 card-surface p-3 animate-pulse">
          <div className={`${h} bg-stone-200 rounded-lg mb-3`}></div>
          <div className="h-4 bg-stone-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-stone-200 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  );
}
import { ProviderCard } from '../components/discovery/ProviderCard';

const CATEGORIES = [
  { id: 'all', name: 'All Services' },
  ...CATEGORY_DEFS.map((c) => ({ id: c.id, name: c.name })),
];

const SERVICE_TYPES = [
  { id: 'all', name: 'All Types' },
  { id: 'salon', name: 'Salon / Studio', icon: Building2 },
  { id: 'home', name: 'Home-based', icon: Home },
  { id: 'mobile', name: 'Mobile', icon: Car }
];

export default function Discovery() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Tutorial walk-through: when this URL flag is set, ask the API to
  // include the demo accounts in Discover. Off by default so real
  // customers never see them.
  const includeTutorials = searchParams.get('include_tutorials') === 'true';
  const { user } = useAuth();
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [specialty, setSpecialty] = useState(searchParams.get('specialty') || '');
  const [coOp, setCoOp] = useState(searchParams.get('co_op') === 'true');
  const [serviceType, setServiceType] = useState(searchParams.get('type') || 'all');
  const [maxPrice, setMaxPrice] = useState([500]);
  const [location, setLocation] = useState(searchParams.get('location') || '');
  // City-centre radius filter driven by the homepage CityPinTicker.
  // When `?city=sheffield` is present, the grid shows only providers
  // within 20 mi of that city's centre postcode.
  const cityFilter = (searchParams.get('city') || '').trim().toLowerCase();
  const [cityFilterData, setCityFilterData] = useState(null);

  // URL back-compat: if someone lands on `?category=hair` (legacy 4-bucket
  // taxonomy), upgrade the URL + state to the new 7-pillar id in-place so
  // shared links and old SEO crawls don't dead-end on an empty grid.
  useEffect(() => {
    const raw = searchParams.get('category');
    if (raw && LEGACY_CATEGORY_MAP[raw]) {
      const mapped = LEGACY_CATEGORY_MAP[raw];
      setCategory(mapped);
      const next = new URLSearchParams(searchParams);
      next.set('category', mapped);
      setSearchParams(next, { replace: true });
    } else if (raw && raw !== 'all' && !resolveCategoryId(raw)) {
      // Unknown id (typo, removed pillar). Drop back to 'all' silently.
      setCategory('all');
      const next = new URLSearchParams(searchParams);
      next.delete('category');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Postcode search state
  const [postcode, setPostcode] = useState('');
  const [nearbyMode, setNearbyMode] = useState(false);
  const [searchingPostcode, setSearchingPostcode] = useState(false);

  // "Near You" postcode-distance sort. Separate from the mobile-nearby
  // search above because it affects a curated row rather than the
  // whole result set. Persisted in localStorage so returning visitors
  // don't have to retype it.
  const [nearYouPostcode, setNearYouPostcode] = useState(() => {
    try { return window.localStorage.getItem('zuriya_near_you_postcode') || ''; }
    catch { return ''; }
  });
  const [nearYouDraft, setNearYouDraft] = useState(nearYouPostcode);

  // Curated browse data
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(true);

  // Favourites
  const [favouritedIds, setFavouritedIds] = useState(new Set());

  const isCategoryOnly = (category && category !== 'all') && !search && !specialty && !coOp && (serviceType === 'all' || !serviceType) && maxPrice[0] >= 500 && !nearbyMode && !cityFilter;
  const isFiltering = search || specialty || coOp || (category && category !== 'all') || (serviceType && serviceType !== 'all') || maxPrice[0] < 500 || nearbyMode || cityFilter;

  useEffect(() => {
    fetchBrowseData();
    // fetchBrowseData is a stable function defined in-component; it would
    // cause an infinite loop if added to deps because React re-creates it
    // on every render. One-shot browse fetch is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearYouPostcode]);

  useEffect(() => {
    if (user && user.role !== 'provider' && user.role !== 'admin') {
      api.get('/favourites').then(res => {
        setFavouritedIds(new Set((res.data || []).map(f => f.provider_id)));
      }).catch(() => {});
    }
  }, [user]);

  const toggleFavourite = useCallback(async (providerId) => {
    const isFav = favouritedIds.has(providerId);
    setFavouritedIds(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
    try {
      if (isFav) {
        await api.delete(`/favourites/${providerId}`);
        toast.success('Removed from favourites');
      } else {
        await api.post(`/favourites/${providerId}`);
        toast.success('Added to favourites');
      }
    } catch (err) {
      console.error('Favourite toggle error:', err);
      setFavouritedIds(prev => {
        const next = new Set(prev);
        if (isFav) next.add(providerId);
        else next.delete(providerId);
        return next;
      });
      toast.error('Failed to update favourite');
    }
  }, [favouritedIds]);

  useEffect(() => {
    if (isFiltering && !isCategoryOnly) {
      if (!nearbyMode && !cityFilter) fetchProviders();
    }
    // fetchProviders is an in-component function, adding it would re-fetch
    // on every render. `isFiltering` and `isCategoryOnly` are derived from
    // the deps already listed so adding them is redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, serviceType, maxPrice, specialty, coOp]);

  // CityPinTicker click -> /discover?city=sheffield. Fetch providers
  // within 20 mi of that city's centre postcode and show them as a
  // filtered grid. Clearing the city param restores normal browse.
  useEffect(() => {
    if (!cityFilter) {
      setCityFilterData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/discover/by-city', { params: { city: cityFilter } });
        if (cancelled) return;
        setCityFilterData(res.data);
        setFilteredProviders(res.data.providers || []);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || 'Could not load providers for that city.';
        toast.error(detail);
        setCityFilterData({ city: cityFilter, radius_miles: 20, providers: [], count: 0, error: true });
        setFilteredProviders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cityFilter]);

  const fetchBrowseData = async (retries = 5) => {
    try {
      const params = nearYouPostcode ? { postcode: nearYouPostcode } : {};
      const res = await api.get('/discover/browse', { params });
      setBrowseData(res.data);
      setBrowseLoading(false);
    } catch (err) {
      const status = err.response?.status;
      const isTransient = status === 503 || status === 502 || !err.response;
      if (retries > 0 && isTransient) {
        await new Promise(r => setTimeout(r, 3000));
        return fetchBrowseData(retries - 1);
      }
      console.error('Error fetching browse data:', err);
      setBrowseLoading(false);
    }
  };

  const fetchProviders = async (retries = 3) => {
    setLoading(true);
    setNearbyMode(false);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (serviceType && serviceType !== 'all') params.append('service_type', serviceType);
      if (specialty) params.append('specialty', specialty);
      if (coOp) params.append('co_op', 'true');
      if (maxPrice[0] < 500) params.append('max_price', maxPrice[0]);
      if (search) params.append('search', search);
      if (includeTutorials) params.append('include_tutorials', 'true');
      const response = await api.get(`/discover/search?${params.toString()}`);
      setFilteredProviders(response.data);
      setLoading(false);
    } catch (error) {
      const status = error.response?.status;
      const isTransient = status === 503 || status === 502 || !error.response;
      if (retries > 0 && isTransient) {
        await new Promise(r => setTimeout(r, 3000));
        return fetchProviders(retries - 1);
      }
      console.error('Error fetching providers:', error);
      setLoading(false);
    }
  };

  const searchNearby = async (e) => {
    if (e) e.preventDefault();
    if (!postcode.trim()) { toast.error('Please enter a postcode'); return; }
    setSearchingPostcode(true);
    setLoading(true);
    try {
      const params = new URLSearchParams({ postcode: postcode.trim() });
      if (category && category !== 'all') params.append('category', category);
      if (specialty) params.append('specialty', specialty);
      if (coOp) params.append('co_op', 'true');
      if (maxPrice[0] < 500) params.append('max_price', maxPrice[0]);
      if (search) params.append('search', search);
      if (includeTutorials) params.append('include_tutorials', 'true');
      const response = await api.get(`/discover/search/nearby?${params.toString()}`);
      setFilteredProviders(response.data.providers || []);
      setNearbyMode(true);
      if ((response.data.providers || []).length === 0) {
        toast.info('No mobile stylists cover your area yet. Try a different postcode or browse all providers.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid postcode');
    } finally {
      setLoading(false);
      setSearchingPostcode(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (nearbyMode) { searchNearby(); } else { fetchProviders(); }
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setSpecialty('');
    setCoOp(false);
    setServiceType('all');
    setMaxPrice([500]);
    setLocation('');
    setPostcode('');
    setNearbyMode(false);
    setFilteredProviders([]);
    setSearchParams({});
  };

  // When only category is selected, filter providers from browse data
  const categoryProviders = React.useMemo(() => {
    if (!isCategoryOnly || !browseData) return [];
    const allProviders = [
      ...(browseData.featured || []),
      ...(browseData.new_on_zuriya || []),
      ...(browseData.near_you || []),
      ...(browseData.trending || []),
    ];
    // Dedupe by provider_id
    const seen = new Set();
    const unique = [];
    for (const p of allProviders) {
      if (!seen.has(p.provider_id)) {
        seen.add(p.provider_id);
        if (p.categories?.some((c) => (resolveCategoryId(c) || resolveCategoryId(String(c).toLowerCase())) === category)) unique.push(p);
      }
    }
    return unique;
  }, [isCategoryOnly, browseData, category]);

  function renderContentArea() {
    if (!isFiltering) {
      if (browseLoading) {
        return <SkeletonGrid keyPrefix="browse" />;
      }
      return (
        <div data-testid="curated-browse">
          <CuratedSection title="Featured Providers" subtitle="Handpicked top-quality profiles vetted by Zuriya" icon={Star} providers={browseData?.featured} testId="section-featured" favouritedIds={favouritedIds} onToggleFavourite={toggleFavourite} isLoggedIn={!!user} />
          <CuratedSection title="New on Zuriya" subtitle="Recently joined, give them a warm welcome" icon={UserPlus} providers={browseData?.new_on_zuriya} testId="section-new" favouritedIds={favouritedIds} onToggleFavourite={toggleFavourite} isLoggedIn={!!user} />
          <CuratedSection
            title="Near You"
            subtitle={nearYouPostcode ? `Sorted by distance from ${nearYouPostcode.toUpperCase()}` : "Set your postcode to see nearest first"}
            icon={MapPin}
            providers={browseData?.near_you}
            testId="section-near"
            favouritedIds={favouritedIds}
            onToggleFavourite={toggleFavourite}
            isLoggedIn={!!user}
            headerAction={
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const cleaned = nearYouDraft.trim().toUpperCase();
                  setNearYouPostcode(cleaned);
                  try { window.localStorage.setItem('zuriya_near_you_postcode', cleaned); } catch {}
                }}
                className="flex items-center gap-1"
                data-testid="near-you-postcode-form"
              >
                <input
                  type="text"
                  inputMode="text"
                  autoComplete="postal-code"
                  value={nearYouDraft}
                  onChange={e => setNearYouDraft(e.target.value)}
                  placeholder="Your postcode"
                  aria-label="Your postcode"
                  className="w-32 px-3 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 placeholder:text-stone-400"
                  data-testid="near-you-postcode-input"
                />
                {nearYouPostcode && (
                  <button
                    type="button"
                    onClick={() => {
                      setNearYouDraft('');
                      setNearYouPostcode('');
                      try { window.localStorage.removeItem('zuriya_near_you_postcode'); } catch {}
                    }}
                    className="text-xs text-stone-400 hover:text-[#1A1A1A] px-1"
                    data-testid="near-you-postcode-clear"
                    aria-label="Clear postcode"
                  >
                    &times;
                  </button>
                )}
              </form>
            }
          />
          <CuratedSection title="Trending" subtitle="Most popular based on reviews and bookings" icon={TrendingUp} providers={browseData?.trending} testId="section-trending" favouritedIds={favouritedIds} onToggleFavourite={toggleFavourite} isLoggedIn={!!user} />
          {browseData && !browseData?.featured?.length && !browseData?.new_on_zuriya?.length && !browseData?.trending?.length && (
            <div className="text-center py-16">
              <Sparkles className="h-12 w-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 text-lg mb-2">No providers yet</p>
              <p className="text-stone-400 text-sm">Check back soon, we're growing!</p>
            </div>
          )}
        </div>
      );
    }

    if (isCategoryOnly) {
      if (browseLoading) {
        return <SkeletonGrid keyPrefix="cat" />;
      }
      if (categoryProviders.length === 0) {
        return (
          <div className="text-center py-16">
            <p className="text-stone-500 text-lg mb-4">No providers found for this category</p>
            <Button onClick={clearFilters} className="btn-secondary">Clear filters</Button>
          </div>
        );
      }
      return (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">
              {CATEGORIES.find(c => c.id === category)?.name || 'Providers'} <span className="text-stone-400 font-normal text-base">({categoryProviders.length})</span>
            </h2>
          </div>
          <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 lg:gap-3" data-testid="category-providers-grid">
            {categoryProviders.map((provider) => (
              <div key={provider.provider_id} className="break-inside-avoid mb-2 lg:mb-3">
                <ProviderCard provider={provider} isFavourited={favouritedIds.has(provider.provider_id)} onToggleFavourite={toggleFavourite} isLoggedIn={!!user} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Provider-level search results
    if (loading) {
      return <SkeletonGrid keyPrefix="filter" />;
    }
    if (filteredProviders.length === 0) {
      return (
        <div className="text-center py-16">
          <p className="text-stone-500 text-lg mb-4">No providers found</p>
          <Button onClick={clearFilters} className="btn-secondary">Clear filters</Button>
        </div>
      );
    }
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">
            {nearbyMode ? 'Mobile Stylists Near You' : 'Results'} <span className="text-stone-400 font-normal text-base">({filteredProviders.length} provider{filteredProviders.length !== 1 ? 's' : ''})</span>
          </h2>
        </div>
        <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 lg:gap-3" data-testid="search-providers-grid">
          {filteredProviders.map((provider) => (
            <div key={provider.provider_id} className="break-inside-avoid mb-2 lg:mb-3">
              <ProviderCard provider={provider} isFavourited={favouritedIds.has(provider.provider_id)} onToggleFavourite={toggleFavourite} isLoggedIn={!!user} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // SkeletonGrid definition hoisted to module scope above.

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title="Discover Services"
        description="Find and book trusted inclusive multi-cultural hair, nails, lashes and beauty services near you. Browse by category, location, and price."
        url={`${window.location.origin}/discover`}
      />
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {includeTutorials && (
          <div
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
            data-testid="discover-tutorial-mode-banner"
          >
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-amber-200 text-amber-900 text-xs font-bold flex-shrink-0">i</span>
            <div className="text-sm text-amber-900 flex-1">
              <span className="font-semibold">Tutorial mode active.</span> Demo
              providers are visible for filming. Real customers do not see them.
            </div>
            <a
              href="/discover"
              className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              data-testid="discover-exit-tutorial-mode"
            >
              Exit tutorial mode
            </a>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-2" data-testid="discovery-title">
            Find Your Stylist
          </h1>
          <p className="text-stone-500">Book trusted multi-cultural beauty professionals across the UK</p>
        </div>

        {/* Find Mobile Stylists Near You */}
        <div className="card-surface p-5 mb-6 border-l-4 border-l-[#D4AF37]" data-testid="postcode-search-section">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                  <Navigation className="h-4 w-4 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1A1A1A] text-sm">Find Mobile Stylists Near You</h3>
                  <p className="text-xs text-stone-400">Enter your postcode to see who covers your area</p>
                </div>
              </div>
            </div>
            <form onSubmit={searchNearby} className="flex gap-2 flex-1 md:max-w-md md:ml-auto">
              <Input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                placeholder="e.g. SW9 8LQ"
                className="input-zuriya uppercase font-mono tracking-wider"
                maxLength={8}
                data-testid="postcode-input"
              />
              <Button type="submit" className="btn-primary whitespace-nowrap" disabled={searchingPostcode || !postcode.trim()} data-testid="postcode-search-btn">
                <Car className="h-4 w-4 mr-1" />
                {searchingPostcode ? 'Searching...' : 'Search'}
              </Button>
            </form>
          </div>
          {nearbyMode && (
            <div className="mt-3 pt-3 border-t border-[#E6E4DD] flex items-center justify-between">
              <p className="text-sm text-[#D4AF37] font-medium flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                Showing mobile stylists near {postcode}
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-stone-500 text-xs" data-testid="clear-postcode-btn">
                <X className="h-3 w-3 mr-1" />
                Show all services
              </Button>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="card-surface p-4 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services..." className="input-zuriya pl-12" data-testid="search-input" />
            </div>
            <Select value={category} onValueChange={(v) => { setCategory(v); setSpecialty(''); }}>
              <SelectTrigger className="w-full md:w-[180px] input-zuriya" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="w-full md:w-[180px] input-zuriya" data-testid="service-type-filter"><SelectValue placeholder="Service Type" /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((type) => (<SelectItem key={type.id} value={type.id}><span className="flex items-center gap-2">{type.icon && <type.icon className="h-4 w-4" />}{type.name}</span></SelectItem>))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" className="md:hidden" onClick={() => setShowFilters(!showFilters)}><Filter className="h-4 w-4 mr-2" />Filters</Button>
            <Button type="submit" className="btn-primary" data-testid="search-submit">Search</Button>
          </form>
          <SpecialtyFilter category={category} specialty={specialty} setSpecialty={setSpecialty} coOp={coOp} setCoOp={setCoOp} />

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#E6E4DD] md:hidden">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#1A1A1A] mb-2 block">Max Price: £{maxPrice[0]}</label>
                  <Slider value={maxPrice} onValueChange={setMaxPrice} max={500} step={10} className="w-full" />
                </div>
                <Button variant="ghost" onClick={clearFilters} className="text-stone-500"><X className="h-4 w-4 mr-2" />Clear filters</Button>
              </div>
            </div>
          )}
          <div className="hidden md:flex items-center gap-6 mt-4 pt-4 border-t border-[#E6E4DD]">
            <div className="flex items-center gap-4 flex-1">
              <span className="text-sm font-medium text-[#1A1A1A]">Max Price: £{maxPrice[0]}</span>
              <Slider value={maxPrice} onValueChange={setMaxPrice} max={500} step={10} className="w-48" data-testid="price-filter" />
            </div>
            <Button variant="ghost" onClick={clearFilters} className="text-stone-500" data-testid="clear-filters"><X className="h-4 w-4 mr-2" />Clear filters</Button>
          </div>
        </div>

        {/* City-filter chip, shown when ?city=... is in the URL. */}
        {cityFilter && cityFilterData && (
          <div
            data-testid="city-filter-chip"
            className="mb-4 inline-flex items-center gap-3 bg-[#1A1A1A] text-white rounded-full pl-4 pr-2 py-1.5"
          >
            <MapPin className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-sm">
              {cityFilterData.count > 0 ? (
                <>Showing <strong>{cityFilterData.count}</strong> provider{cityFilterData.count === 1 ? '' : 's'} within {cityFilterData.radius_miles} mi of <strong>{cityFilterData.city}</strong></>
              ) : (
                <>No providers within {cityFilterData.radius_miles || 20} mi of <strong>{cityFilterData.city || cityFilter}</strong> yet</>
              )}
            </span>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('city');
                setSearchParams(next);
              }}
              className="w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center"
              data-testid="city-filter-clear"
              aria-label="Clear city filter"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* CONTENT AREA */}
        {renderContentArea()}
      </div>
    </div>
  );
}
